import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const toMoney = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "Q0.00";
  return `Q${parsed.toFixed(2)}`;
};

function PriceOverrideDialog({ open, data, onCancel, onConfirm, submitting = false }) {
  const basePrice = Number(data?.basePrice || 0);
  const costPrice = Number(data?.costPrice || 0);
  const enteredPrice = Number(data?.newUnitPrice ?? basePrice);
  const [draftPrice, setDraftPrice] = useState(
    Number.isFinite(enteredPrice) ? enteredPrice.toFixed(2) : basePrice.toFixed(2)
  );

  useEffect(() => {
    if (!open) return;
    const nextPrice = Number(data?.newUnitPrice ?? data?.basePrice ?? 0);
    setDraftPrice(Number.isFinite(nextPrice) ? nextPrice.toFixed(2) : "0.00");
  }, [data?.basePrice, data?.newUnitPrice, open]);

  const pricingState = useMemo(() => {
    const normalizedRaw = String(draftPrice ?? "").trim();
    const parsed = Number(normalizedRaw);
    const hasValue = normalizedRaw !== "" && Number.isFinite(parsed);
    const safePrice = hasValue ? parsed : 0;
    const utility = safePrice - costPrice;
    const margin = safePrice > 0 ? (utility / safePrice) * 100 : 0;
    const belowCost = hasValue && safePrice < costPrice;
    return {
      hasValue,
      safePrice,
      utility,
      margin,
      belowCost,
    };
  }, [costPrice, draftPrice]);

  const confirmDisabled =
    submitting || !pricingState.hasValue || pricingState.belowCost;

  return (
    <Dialog
      open={Boolean(open)}
      onClose={(_, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onCancel();
      }}
      fullWidth
      maxWidth="xs"
      keepMounted
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 20,
      }}
    >
      <DialogTitle>Confirmar baja de precio</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.4} sx={{ pt: 0.4 }}>
          <Typography variant="body2" color="text.secondary">
            Estas confirmando un precio menor al precio base.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 1,
            }}
          >
            <Card variant="outlined">
              <CardContent sx={{ py: 1.1, "&:last-child": { pb: 1.1 } }}>
                <Typography variant="caption" color="text.secondary">
                  Precio base
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {toMoney(basePrice)}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.1, "&:last-child": { pb: 1.1 } }}>
                <Typography variant="caption" color="text.secondary">
                  Costo
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {toMoney(costPrice)}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.1, "&:last-child": { pb: 1.1 } }}>
                <Typography variant="caption" color="text.secondary">
                  Precio ingresado
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, color: "warning.main" }}>
                  {toMoney(data?.newUnitPrice)}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <TextField
            label="Precio de venta"
            size="small"
            type="number"
            value={draftPrice}
            onChange={(event) => setDraftPrice(event.target.value)}
            inputProps={{ step: "0.01", min: 0 }}
            error={pricingState.belowCost}
            helperText={pricingState.belowCost ? "No puede vender por debajo del costo" : " "}
            fullWidth
          />

          <Stack spacing={0.2}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Utilidad: {toMoney(pricingState.utility)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Margen: {Number.isFinite(pricingState.margin) ? pricingState.margin.toFixed(2) : "0.00"}%
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => onConfirm(pricingState.safePrice)}
          disabled={confirmDisabled}
        >
          Aceptar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PriceOverrideDialog;
